import CONFIG from '../config';
import Logger from '../utils/logger';

export type MarketingEvent =
  | 'page_view'
  | 'demo_prompt'
  | 'trinity_response'
  | 'answer_helpful'
  | 'answer_needs_work'
  | 'vidya_click'
  | 'free_game_open'
  | 'get_dubya_click'
  | 'verify_mint_click'
  | 'rewards_transparency_click'
  | 'follow_build_click'
  | 'share_build_click'
  | 'share_answer_click'
  | 'dubya_interest_yes'
  | 'dubya_interest_not_yet'
  | 'insert_interest_yes'
  | 'insert_interest_not_yet'
  | 'insert_plan_click'
  | 'copy_challenge_click';

const SESSION_KEY = 'dubya:marketing-session:v1';
const DIMENSION_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const VIDEO_CAMPAIGN_PATTERN = /^dubya_video_(?:0[1-9]|1[0-9]|20)$/;
const X_ORGANIC_CAMPAIGNS = new Set([
  'shared_answer',
  'shared_build',
  'shared_game',
  'community_reply_01',
]);
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let pageViewSent = false;

function analyticsDisabled(params: URLSearchParams): boolean {
  const values = params.getAll('analytics');
  return values.length === 1 && values[0] === 'off';
}

function createSessionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getSessionId(): string | null {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing && UUID_V4_PATTERN.test(existing)) return existing;
    const sessionId = createSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
    return sessionId;
  } catch {
    return null;
  }
}

function dimension(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name);
  return value && DIMENSION_PATTERN.test(value) ? value : undefined;
}

function campaignDimensions(params: URLSearchParams, pathname: string): {
  source?: string;
  medium?: string;
  campaign?: string;
} {
  const hasUtmParameters =
    params.has('utm_source') || params.has('utm_medium') || params.has('utm_campaign');
  if (!hasUtmParameters && (pathname === '/try' || pathname === '/try/')) {
    return { source: 'pump', medium: 'livestream', campaign: 'trinity_live' };
  }
  if (
    params.getAll('utm_source').length > 1 ||
    params.getAll('utm_medium').length > 1 ||
    params.getAll('utm_campaign').length > 1
  ) {
    return {};
  }
  const source = dimension(params, 'utm_source');
  const medium = dimension(params, 'utm_medium');
  const campaign = dimension(params, 'utm_campaign');
  if (!source && !medium && !campaign) return {};

  const reviewed =
    (source === 'x' && medium === undefined && campaign === 'trust') ||
    (medium === 'organic' &&
      VIDEO_CAMPAIGN_PATTERN.test(campaign || '') &&
      (source === 'x' || source === 'tiktok')) ||
    (source === 'x' && medium === 'organic' && X_ORGANIC_CAMPAIGNS.has(campaign || '')) ||
    (source === 'direct' &&
      medium === 'referral' &&
      (campaign === 'shared_answer' || campaign === 'shared_game')) ||
    (source === 'direct' && medium === 'referral' && campaign === 'insert_challenge') ||
    (source === 'x' && medium === 'organic' && campaign === 'insert_shared_answer') ||
    (source === 'pump' && medium === 'livestream' && campaign === 'trinity_live') ||
    (source === 'x' && medium === 'profile' && campaign === 'dubya_profile') ||
    (source === 'trinity' && medium === 'product' && campaign === 'dubya_ecosystem') ||
    (source === 'trinity' && medium === 'product' && campaign === 'trinity_access') ||
    (source === 'vidya' && medium === 'product' && campaign === 'dubya_beta');

  return reviewed ? { source, medium, campaign } : {};
}

export function trackMarketingEvent(event: MarketingEvent): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (analyticsDisabled(params)) return;
  const sessionId = getSessionId();
  if (!sessionId) return;
  const body = {
    event,
    sessionId,
    page: 'trinity',
    ...campaignDimensions(params, window.location.pathname),
  };

  void fetch(`${CONFIG.API_URL}/analytics/event`, {
    method: 'POST',
    credentials: 'omit',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok) Logger.debug(`Marketing event rejected: ${response.status}`);
    })
    .catch((error) => Logger.debug('Marketing event unavailable:', error));
}

export function trackTrinityPageViewOnce(): void {
  if (pageViewSent) return;
  pageViewSent = true;
  trackMarketingEvent('page_view');
}

export function resetMarketingAnalyticsForTests(): void {
  pageViewSent = false;
}
